import { motion } from "framer-motion"
import { useState } from "react"
import {
  ChevronDownIcon,
  ChevronRightIcon,
  RocketLaunchIcon,
} from "@heroicons/react/24/outline"
import {
  AdChannel,
  MetaObjective,
  MetaOptimizationGoal,
  TiktokGoal,
  PinterestGoal,
  GoogleAdsOptimizationGoal,
  OptimizationGoal,
} from "~/ui-rtk/api/types/custom-attribution-enums"
import { toCurrency } from "~/ui-rtk/utils/number-utils"

interface GoalSelectorProps {
  objective?: MetaObjective
  adChannel?: AdChannel
  onGoalSelect: (goal: OptimizationGoal) => void
  error?: string
  touched?: boolean
  adsetData?: Record<string, string | undefined>[]
}

export function GoalSelector({
  objective,
  adChannel,
  onGoalSelect,
  error,
  touched,
  adsetData = [],
}: GoalSelectorProps) {
  // State for tracking expanded items
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())

  // Calculate total spend for this channel to get percentages
  const totalChannelSpend = adsetData
    .filter((row: Record<string, string | undefined>) => {
      const rowChannel =
        row["company_media_metrics_adsets.media_channel"]?.toLowerCase()
      return rowChannel === adChannel?.toLowerCase()
    })
    .reduce((total: number, row: Record<string, string | undefined>) => {
      const spend = parseFloat(row["company_media_metrics_adsets.spend"] || "0")
      return total + spend
    }, 0)

  // Calculate spending for each goal
  const getSpendingForGoal = (goal: string) => {
    if (!adsetData || adsetData.length === 0) return 0

    // Filter data by the same channel
    const channelData = adsetData.filter(
      (row: Record<string, string | undefined>) => {
        const rowChannel =
          row["company_media_metrics_adsets.media_channel"]?.toLowerCase()
        return rowChannel === adChannel?.toLowerCase()
      },
    )

    // Sum spending for this specific combination
    return channelData.reduce(
      (total: number, row: Record<string, string | undefined>) => {
        const spend = parseFloat(
          row["company_media_metrics_adsets.spend"] || "0",
        )
        const rowObjective = row["company_media_metrics_adsets.objective"] || ""
        const rowGoal =
          row["company_media_metrics_adsets.optimization_goal"] || ""

        let shouldInclude = false

        if (
          adChannel === AdChannel.TIKTOK ||
          adChannel === AdChannel.PINTEREST ||
          adChannel === AdChannel.GOOGLE_ADS ||
          adChannel === AdChannel.YOUTUBE
        ) {
          // TikTok / Pinterest / Google Ads / YouTube: only match optimization_goal (no objectives)
          shouldInclude = rowGoal === goal
        } else {
          // For Meta: match both objective AND optimization_goal
          const objectiveMatch = !objective || rowObjective === objective
          const goalMatch = rowGoal === goal
          shouldInclude = objectiveMatch && goalMatch
        }

        if (shouldInclude) {
          return total + spend
        }
        return total
      },
      0,
    )
  }

  // Get base goals by channel; Google Ads and YouTube use same enum
  let goalEnum: Record<string, string> = MetaOptimizationGoal
  if (adChannel === AdChannel.TIKTOK) {
    goalEnum = TiktokGoal
  } else if (adChannel === AdChannel.PINTEREST) {
    goalEnum = PinterestGoal
  } else if (
    adChannel === AdChannel.GOOGLE_ADS ||
    adChannel === AdChannel.YOUTUBE
  ) {
    goalEnum = GoogleAdsOptimizationGoal
  }
  const baseGoals = Object.values(goalEnum)

  const goals = baseGoals.sort((a, b) => {
    const spendingA = getSpendingForGoal(a)
    const spendingB = getSpendingForGoal(b)
    return spendingB - spendingA
  })

  // Get campaigns for a specific goal
  const getCampaignsForGoal = (goal: string) => {
    if (!adsetData || adsetData.length === 0) return []

    // Filter data by the same channel
    const channelData = adsetData.filter(
      (row: Record<string, string | undefined>) => {
        const rowChannel =
          row["company_media_metrics_adsets.media_channel"]?.toLowerCase()
        return rowChannel === adChannel?.toLowerCase()
      },
    )

    let filteredData = []
    if (
      adChannel === AdChannel.TIKTOK ||
      adChannel === AdChannel.PINTEREST ||
      adChannel === AdChannel.GOOGLE_ADS ||
      adChannel === AdChannel.YOUTUBE
    ) {
      // TikTok / Pinterest / Google Ads / YouTube: only match optimization_goal
      filteredData = channelData.filter(
        (row: Record<string, string | undefined>) => {
          const rowGoal = row["company_media_metrics_adsets.optimization_goal"]
          return rowGoal === goal
        },
      )
    } else {
      // For Meta: match both objective AND optimization_goal
      filteredData = channelData.filter(
        (row: Record<string, string | undefined>) => {
          const rowObjective =
            row["company_media_metrics_adsets.objective"] || ""
          const rowGoal =
            row["company_media_metrics_adsets.optimization_goal"] || ""
          const objectiveMatch = !objective || rowObjective === objective
          const goalMatch = rowGoal === goal
          return objectiveMatch && goalMatch
        },
      )
    }

    // Group by campaign and sum spending
    const campaignMap = new Map()
    filteredData.forEach((row: Record<string, string | undefined>) => {
      const campaignId = row["company_media_metrics_adsets.campaign_id"]
      const campaignName = row["company_media_metrics_adsets.campaign_name"]
      const spend = parseFloat(row["company_media_metrics_adsets.spend"] || "0")

      if (campaignMap.has(campaignId)) {
        campaignMap.get(campaignId).spend += spend
      } else {
        campaignMap.set(campaignId, {
          id: campaignId,
          name: campaignName,
          spend,
        })
      }
    })

    // Filter campaigns with spending > 0 and sort by spend (highest first)
    return Array.from(campaignMap.values())
      .filter(campaign => campaign.spend > 0)
      .sort((a, b) => b.spend - a.spend)
  }

  // Toggle expanded state
  const toggleExpanded = (goal: string) => {
    const newExpanded = new Set(expandedItems)
    if (newExpanded.has(goal)) {
      newExpanded.delete(goal)
    } else {
      newExpanded.add(goal)
    }
    setExpandedItems(newExpanded)
  }

  const channelDisplayNames: Record<string, string> = {
    [AdChannel.META]: "Meta",
    [AdChannel.TIKTOK]: "TikTok",
    [AdChannel.PINTEREST]: "Pinterest",
    [AdChannel.GOOGLE_ADS]: "Google Ads",
    [AdChannel.YOUTUBE]: "YouTube",
  }
  const channelDisplayName =
    (adChannel && channelDisplayNames[adChannel]) || adChannel

  return (
    <motion.div
      key="tiktok-goal-selection"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{
        opacity: 0,
        transition: {
          duration: 0.15,
          ease: "easeInOut",
        },
      }}
      transition={{
        duration: 0.2,
        ease: "easeInOut",
      }}
      className="space-y-3 px-system-md mt-system-md"
    >
      <div className="text-lg font-semibold text-white mb-3">
        Select Goal for {channelDisplayName}
      </div>
      <div className="grid grid-cols-1 gap-2">
        {goals.map((goal, index) => {
          const spending = getSpendingForGoal(goal)
          const campaigns = getCampaignsForGoal(goal)
          const isExpanded = expandedItems.has(goal)

          return (
            <div key={goal}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{
                  opacity: 0,
                  transition: {
                    duration: 0.1,
                    delay: index * 0.02,
                  },
                }}
                transition={{
                  duration: 0.15,
                  delay: index * 0.05,
                }}
                className="bg-system-blue-600/20 hover:bg-system-blue-600/30 rounded-md border border-system-blue-600/30 hover:border-system-blue-400/50 transition-all duration-200"
              >
                <div className="flex items-center gap-3 p-system-sm">
                  <div className="w-6 h-6 rounded-md bg-system-blue-600/30 flex items-center justify-center transition-colors">
                    <RocketLaunchIcon className="w-4 h-4 text-system-blue-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="text-white font-medium text-base">
                        {goal.replace(/_/g, " ").toLowerCase()}
                      </div>
                      {spending > 0 && (
                        <div className="text-system-cyan-200 font-semibold text-sm text-right">
                          Spend {toCurrency(spending)} last 30 days,{" "}
                          {totalChannelSpend > 0
                            ? ((spending / totalChannelSpend) * 100).toFixed(1)
                            : 0}
                          % of total spend
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-system-gray-400">
                        {channelDisplayName} goal
                      </div>
                      <div className="flex items-center gap-2">
                        {campaigns.length > 0 && (
                          <button
                            onClick={e => {
                              e.stopPropagation()
                              toggleExpanded(goal)
                            }}
                            className="text-system-gray-400 hover:text-white transition-colors p-1"
                          >
                            {isExpanded ? (
                              <ChevronDownIcon className="w-4 h-4" />
                            ) : (
                              <ChevronRightIcon className="w-4 h-4" />
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => onGoalSelect(goal as OptimizationGoal)}
                          className="text-system-blue-400 hover:text-system-blue-300 transition-colors text-sm font-medium"
                        >
                          Select
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Campaigns List */}
                {isExpanded && campaigns.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="border-t border-system-blue-600/30 bg-system-blue-600/10"
                  >
                    <div className="p-3 space-y-2">
                      <div className="text-xs text-system-gray-400 font-medium uppercase tracking-wide">
                        Campaigns ({campaigns.length})
                      </div>
                      {campaigns.map(campaign => (
                        <div
                          key={campaign.id}
                          className="flex items-center justify-between py-1 px-2 rounded bg-system-blue-600/20"
                        >
                          <div className="text-sm text-white truncate">
                            {campaign.name || `Campaign ${campaign.id}`}
                          </div>
                          <div className="text-xs text-system-cyan-200 font-medium">
                            {toCurrency(campaign.spend)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </motion.div>
            </div>
          )
        })}
      </div>
      {error && touched && (
        <p className="text-red-400 text-base mt-2">{error}</p>
      )}
    </motion.div>
  )
}
